import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CloudRain, Sun, CloudSnow, Cloud, Lightning, Thermometer, ArrowUpRight, MapPin } from "@phosphor-icons/react";

/**
 * LiveWorldWeather — 8 city hero band inspired by nature-weather-app.emergent.host.
 * Pulls each city's current temp from the existing /api/weather endpoint.
 * Falls back gracefully when a call errors — the tile just shows "—".
 */
const CITIES = [
  { name: "New York", lat: 40.7128,  lon: -74.0060, tz: "America/New_York" },
  { name: "London",   lat: 51.5074,  lon: -0.1278,  tz: "Europe/London" },
  { name: "Mumbai",   lat: 19.0760,  lon: 72.8777,  tz: "Asia/Kolkata" },
  { name: "Tokyo",    lat: 35.6762,  lon: 139.6503, tz: "Asia/Tokyo" },
  { name: "Sydney",   lat: -33.8688, lon: 151.2093, tz: "Australia/Sydney" },
  { name: "Nairobi",  lat: -1.2921,  lon: 36.8219,  tz: "Africa/Nairobi" },
  { name: "São Paulo",lat: -23.5505, lon: -46.6333, tz: "America/Sao_Paulo" },
  { name: "Reykjavík",lat: 64.1466,  lon: -21.9426, tz: "Atlantic/Reykjavik" },
];

const CODE_TO_ICON = (code) => {
  if (code == null) return { Icon: Sun, label: "—" };
  if ([0].includes(code)) return { Icon: Sun, label: "Clear" };
  if ([1, 2, 3].includes(code)) return { Icon: Cloud, label: "Cloudy" };
  if ([45, 48].includes(code)) return { Icon: Cloud, label: "Foggy" };
  if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return { Icon: CloudRain, label: "Rain" };
  if ([71, 73, 75, 77, 85, 86].includes(code)) return { Icon: CloudSnow, label: "Snow" };
  if ([95, 96, 99].includes(code)) return { Icon: Lightning, label: "Storm" };
  return { Icon: Cloud, label: "—" };
};

export default function LiveWorldWeather() {
  const [rows, setRows] = useState(
    CITIES.map((c) => ({ ...c, current: null, loading: true }))
  );

  useEffect(() => {
    let alive = true;
    CITIES.forEach((c, i) => {
      http.get(`/weather?lat=${c.lat}&lon=${c.lon}&name=${encodeURIComponent(c.name)}`)
        .then(({ data }) => {
          if (!alive) return;
          setRows((rs) => rs.map((r, j) => j === i ? { ...r, current: data?.weather?.current, aqi: data?.air_quality?.current?.us_aqi, loading: false } : r));
        })
        .catch(() => {
          if (!alive) return;
          setRows((rs) => rs.map((r, j) => j === i ? { ...r, loading: false } : r));
        });
    });
    return () => { alive = false; };
  }, []);

  return (
    <section
      className="max-w-6xl mx-auto px-6 md:px-10 mt-12 md:mt-16"
      data-testid="live-world-weather"
    >
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#E4572E)]">
            <Thermometer size={12} weight="fill" className="inline mr-1" />
            Live weather around the world
          </div>
          <h2 className="font-serif-display text-3xl md:text-4xl mt-1">Right now, on planet Earth.</h2>
        </div>
        <Link
          to="/map"
          className="hidden md:inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#E4572E)] hover:underline"
        >View all <ArrowUpRight size={11} weight="bold" /></Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {rows.map((r, idx) => {
          const t = r.current?.temperature_2m;
          const wind = r.current?.wind_speed_10m;
          const code = r.current?.weather_code;
          const { Icon, label } = CODE_TO_ICON(code);
          return (
            <motion.div
              key={r.name}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-20%" }}
              transition={{ duration: 0.4, delay: idx * 0.04 }}
              className="rounded-2xl border border-black/10 bg-white p-4"
              data-testid={`live-city-${r.name.toLowerCase().replace(/[^a-z]/g,'-')}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1 text-[#3A3830] text-[10px] font-mono-tech tracking-[0.22em] uppercase truncate">
                    <MapPin size={10} weight="fill" /> {r.name}
                  </div>
                  <div className="mt-1 flex items-baseline gap-1">
                    <span className="font-serif-display text-4xl leading-none">
                      {r.loading ? "…" : (t != null ? Math.round(t) : "—")}
                    </span>
                    <span className="text-[#8E8B7F] text-sm">°C</span>
                  </div>
                  <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#8E8B7F] mt-0.5">{label}</div>
                </div>
                <Icon size={22} weight="fill" className="text-[color:var(--np-brand,#E4572E)] shrink-0" />
              </div>
              <div className="mt-2 grid grid-cols-2 gap-1 text-[10px] font-mono-tech tracking-[0.18em] uppercase text-[#3A3830]">
                <div>Wind {wind != null ? `${Math.round(wind)}` : "—"}</div>
                <div>AQI {r.aqi != null ? Math.round(r.aqi) : "—"}</div>
              </div>
            </motion.div>
          );
        })}
      </div>
      <div className="mt-3 text-center font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#8E8B7F]">
        Powered by Open-Meteo · Free real-time weather data
      </div>
    </section>
  );
}
